/**
 * Copyright (c) SAGE3 Development Team 2025. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 *
 * Created by Pak Kwan 2026-07-27: NarrativePathView for Phase 1B
 * Displays the current narrative path as an ordered presentation sequence
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  IconButton,
  Tooltip,
  useColorModeValue,
  Collapse,
  Badge,
  Divider,
  useToast,
  Button,
  Editable,
  EditableInput,
  EditablePreview,
} from '@chakra-ui/react';
import { MdExpandMore, MdExpandLess, MdViewList, MdDelete, MdLocationOn, MdPlayArrow } from 'react-icons/md';

import { useThrottleApps, useLinkStore, useHexColor, useUIStore, useUser } from '@sage3/frontend';
import { getNarrativePath } from '@sage3/applications/apps';
import type { App } from '@sage3/applications/schema';

type NarrativePathViewProps = {
  boardId: string;
};

export function NarrativePathView({ boardId }: NarrativePathViewProps) {
  const apps = useThrottleApps(250);
  const links = useLinkStore((s) => s.links);
  const removeLinks = useLinkStore((s) => s.removeLinks);

  // UI Store for viewport control and visibility toggle
  const fitApps = useUIStore((s) => s.fitApps);
  const narrativePathViewVisible = useUIStore((s) => s.narrativePathViewVisible);
  const setPathName = useUIStore((s) => s.setPathName);
  const getPathName = useUIStore((s) => s.getPathName);

  const { user } = useUser();

  // Toast for user feedback
  const toast = useToast();

  // Collapse state
  const [isOpen, setIsOpen] = useState(true);

  // Colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tealColor = useHexColor('teal');
  const grayColor = useColorModeValue('gray.600', 'gray.400');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');

  // Filter apps and links for current board
  const boardApps = useMemo(() => apps.filter((a) => a.data.boardId === boardId), [apps, boardId]);
  const boardLinks = useMemo(() => links.filter((l) => l.data.boardId === boardId), [links, boardId]);

  // Compute all narrative paths on the board
  // A board might have multiple disconnected paths
  const narrativePaths = useMemo(() => {
    const narrativeLinks = boardLinks.filter((l) => l.data.type === 'narrative_order');
    if (narrativeLinks.length === 0) return [];

    // Find all unique paths (start from apps with no incoming narrative links)
    const appsWithIncoming = new Set(narrativeLinks.map((l) => l.data.targetAppId));
    const appsWithOutgoing = new Set(narrativeLinks.map((l) => l.data.sourceAppId));

    // Heads: apps that have outgoing links but no incoming links
    const heads = Array.from(appsWithOutgoing).filter((id) => !appsWithIncoming.has(id));

    // Also check for isolated apps (has incoming but no outgoing) - these are tails
    const tails = Array.from(appsWithIncoming).filter((id) => !appsWithOutgoing.has(id));

    // Build paths from each head
    const paths: string[][] = [];
    const processed = new Set<string>();

    for (const headId of heads) {
      if (!processed.has(headId)) {
        const path = getNarrativePath(headId, boardLinks);
        if (path.length > 0) {
          paths.push(path);
          path.forEach((id) => processed.add(id));
        }
      }
    }

    // Also check from any app not yet processed (handles edge cases)
    for (const app of boardApps) {
      if (!processed.has(app._id)) {
        const hasNarrativeLink = narrativeLinks.some(
          (l) => l.data.sourceAppId === app._id || l.data.targetAppId === app._id
        );
        if (hasNarrativeLink) {
          const path = getNarrativePath(app._id, boardLinks);
          if (path.length > 0) {
            paths.push(path);
            path.forEach((id) => processed.add(id));
          }
        }
      }
    }

    return paths;
  }, [boardApps, boardLinks]);

  // Get app by ID
  const getApp = (appId: string): App | undefined => {
    return boardApps.find((a) => a._id === appId);
  };

  // Compute standalone apps (apps NOT in any narrative path)
  const standaloneApps = useMemo(() => {
    const appsInPaths = new Set<string>();
    narrativePaths.forEach((path) => path.forEach((appId) => appsInPaths.add(appId)));
    return boardApps.filter((app) => !appsInPaths.has(app._id));
  }, [boardApps, narrativePaths]);

  // Total steps across all paths
  const totalSteps = narrativePaths.reduce((sum, path) => sum + path.length, 0);

  // Jump to app: center viewport on the app
  // Phase 2B: If in presentation mode, this triggers a detour; otherwise just centers viewport
  const handleJumpToApp = (appId: string, pathIndex: number, stepIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const app = getApp(appId);
    if (!app) return;

    // Check if we're in presentation mode
    const isPresentationActive = window.__isPresentationActive;

    if (isPresentationActive) {
      // In presentation mode: trigger jump (which may create a detour)
      handleStartPresentation(pathIndex, stepIndex);
    } else {
      // Not in presentation: just center viewport
      fitApps([app]);
      toast({
        title: 'Jumped to App',
        description: `Centered on: ${app.data.title}`,
        status: 'info',
        duration: 1500,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // Delete link: find and remove the link leading TO this app (incoming link)
  const handleDeleteLink = (appId: string, stepIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();

    // If it's the first step, it has no incoming link
    if (stepIndex === 0) {
      toast({
        title: 'Cannot Delete',
        description: 'This is the first step in the path (no incoming link).',
        status: 'warning',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    // Find the incoming narrative link to this app
    const incomingLink = boardLinks.find(
      (l) => l.data.type === 'narrative_order' && l.data.targetAppId === appId
    );

    if (incomingLink) {
      removeLinks([incomingLink._id]);
      toast({
        title: 'Link Deleted',
        description: 'Removed link from presentation path.',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    } else {
      toast({
        title: 'Link Not Found',
        description: 'Could not find the link to delete.',
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // Start presentation handler for a specific path and step
  // Phase 2B: Now supports mid-presentation jumps (detours)
  const handleStartPresentation = (pathIndex: number, stepIndex: number = 0) => {
    if (narrativePaths.length > pathIndex && narrativePaths[pathIndex].length > stepIndex) {
      const targetAppId = narrativePaths[pathIndex][stepIndex];
      // Call the global function exposed by PresentationController
      if (window.__startPresentation) {
        window.__startPresentation(targetAppId, pathIndex, stepIndex);
      }
    }
  };

  // Jump to standalone app (not in any narrative path)
  const handleJumpToStandaloneApp = (appId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const app = getApp(appId);
    if (!app) return;

    // Check if we're in presentation mode
    const isPresentationActive = window.__isPresentationActive;

    if (isPresentationActive) {
      // In presentation mode: trigger detour to standalone app
      if (window.__startPresentation) {
        window.__startPresentation(appId, undefined, 0);
      }
    } else {
      // Not in presentation: just center viewport
      fitApps([app]);
      toast({
        title: 'Jumped to App',
        description: `Centered on: ${app.data.title}`,
        status: 'info',
        duration: 1500,
        isClosable: true,
        position: 'top',
      });
    }
  };


  // Show panel if: (1) has narrative paths OR standalone apps, AND (2) visibility is on
  const hasContent = totalSteps > 0 || standaloneApps.length > 0;
  if (!hasContent || !narrativePathViewVisible) {
    return null;
  }

  return (
    <Box
      position="absolute"
      left="2"
      bottom="20"
      zIndex={100}
      minWidth="280px"
      maxWidth="320px"
      bg={bgColor}
      borderRadius="md"
      borderWidth="1px"
      borderColor={borderColor}
      shadow="lg"
      overflow="hidden"
    >
      {/* Header */}
      <HStack
        px={3}
        py={2}
        bg={tealColor}
        color="white"
        justifyContent="space-between"
        cursor="pointer"
        onClick={() => setIsOpen(!isOpen)}
        _hover={{ opacity: 0.9 }}
      >
        <HStack spacing={2}>
          <MdViewList size={18} />
          <Text fontSize="sm" fontWeight="bold">
            Narrative Path
          </Text>
          <Badge colorScheme="whiteAlpha" fontSize="xs">
            {totalSteps > 0 && `${totalSteps} step${totalSteps !== 1 ? 's' : ''}`}
            {totalSteps > 0 && standaloneApps.length > 0 && ' + '}
            {standaloneApps.length > 0 && `${standaloneApps.length} other`}
          </Badge>
        </HStack>
        <IconButton
          aria-label="Toggle path view"
          icon={isOpen ? <MdExpandLess /> : <MdExpandMore />}
          size="xs"
          variant="ghost"
          color="white"
          _hover={{ bg: 'whiteAlpha.300' }}
        />
      </HStack>

      {/* Content */}
      <Collapse in={isOpen} animateOpacity>
        <VStack
          align="stretch"
          spacing={0}
          maxHeight="400px"
          overflowY="auto"
          py={2}
        >
          {narrativePaths.map((path, pathIndex) => (
            <Box key={pathIndex}>
              {pathIndex > 0 && <Divider my={2} />}
              <VStack px={3} py={2} spacing={2} align="stretch">
                <HStack justifyContent="space-between" alignItems="center">
                  <Editable
                    defaultValue={getPathName(boardId, pathIndex) || `Path ${pathIndex + 1}`}
                    placeholder={`Path ${pathIndex + 1}`}
                    onSubmit={(value) => setPathName(boardId, pathIndex, value)}
                    fontSize="xs"
                    fontWeight="bold"
                    color={grayColor}
                    flex={1}
                    minW="80px"
                  >
                    <EditablePreview cursor="pointer" _hover={{ bg: hoverBgColor }} px={1} py={0.5} borderRadius="sm" />
                    <EditableInput px={1} py={0.5} fontSize="xs" />
                  </Editable>
                  <Button
                    leftIcon={<MdPlayArrow />}
                    colorScheme="teal"
                    size="xs"
                    onClick={() => handleStartPresentation(pathIndex)}
                    flexShrink={0}
                  >
                    Start{narrativePaths.length === 1 ? ' Presentation' : ''}
                  </Button>
                </HStack>
              </VStack>
              {path.map((appId, stepIndex) => {
                const app = getApp(appId);
                if (!app) return null;

                return (
                  <HStack
                    key={appId}
                    px={3}
                    py={2}
                    spacing={2}
                    _hover={{ bg: hoverBgColor }}
                    transition="background 0.2s"
                  >
                    <Badge colorScheme="teal" fontSize="xs" minWidth="24px" textAlign="center">
                      {stepIndex + 1}
                    </Badge>
                    <VStack
                      align="start"
                      spacing={0}
                      flex={1}
                      overflow="hidden"
                      cursor="pointer"
                      onClick={(e) => handleJumpToApp(appId, pathIndex, stepIndex, e)}
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        noOfLines={1}
                        width="100%"
                      >
                        {app.data.title}
                      </Text>
                      <Text
                        fontSize="xs"
                        color={grayColor}
                        noOfLines={1}
                      >
                        {app.data.type}
                      </Text>
                    </VStack>
                    <HStack spacing={0}>
                      <Tooltip label="Jump to app" placement="top" hasArrow>
                        <IconButton
                          aria-label="Jump to app"
                          icon={<MdLocationOn />}
                          size="xs"
                          variant="ghost"
                          colorScheme="teal"
                          onClick={(e) => handleJumpToApp(appId, pathIndex, stepIndex, e)}
                        />
                      </Tooltip>
                      <Tooltip
                        label={stepIndex === 0 ? "First step (no incoming link)" : "Delete incoming link"}
                        placement="top"
                        hasArrow
                      >
                        <IconButton
                          aria-label="Delete link"
                          icon={<MdDelete />}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={(e) => handleDeleteLink(appId, stepIndex, e)}
                          isDisabled={stepIndex === 0}
                        />
                      </Tooltip>
                    </HStack>
                  </HStack>
                );
              })}
            </Box>
          ))}

          {/* Standalone Apps Section (Phase 2B: Q&A content not in narrative paths) */}
          {standaloneApps.length > 0 && (
            <Box>
              <Divider my={2} />
              <HStack px={3} py={2} justifyContent="space-between" alignItems="center">
                <Text fontSize="xs" fontWeight="bold" color={grayColor}>
                  Other Apps ({standaloneApps.length})
                </Text>
              </HStack>
              {standaloneApps.map((app) => (
                <HStack
                  key={app._id}
                  px={3}
                  py={2}
                  spacing={2}
                  _hover={{ bg: hoverBgColor }}
                  transition="background 0.2s"
                  cursor="pointer"
                  onClick={(e) => handleJumpToStandaloneApp(app._id, e)}
                >
                  <Badge colorScheme="gray" fontSize="xs" minWidth="24px" textAlign="center">
                    •
                  </Badge>
                  <VStack
                    align="start"
                    spacing={0}
                    flex={1}
                    overflow="hidden"
                  >
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      noOfLines={1}
                      width="100%"
                    >
                      {app.data.title}
                    </Text>
                    <Text
                      fontSize="xs"
                      color={grayColor}
                      noOfLines={1}
                    >
                      {app.data.type}
                    </Text>
                  </VStack>
                  <Tooltip label="Jump to app" placement="top" hasArrow>
                    <IconButton
                      aria-label="Jump to app"
                      icon={<MdLocationOn />}
                      size="xs"
                      variant="ghost"
                      colorScheme="gray"
                      onClick={(e) => handleJumpToStandaloneApp(app._id, e)}
                    />
                  </Tooltip>
                </HStack>
              ))}
            </Box>
          )}
        </VStack>
      </Collapse>
    </Box>
  );
}
